<template>
  <div>
    <b-row class="mb-2">
      <b-col cols="12" class="d-flex align-items-center justify-content-between flex-wrap">
        <h3 class="mb-0">Assinaturas de Vendedores</h3>
        <b-button variant="primary" @click="abrirModalCriar">
          <feather-icon icon="PlusIcon" class="mr-50" />Nova Assinatura
        </b-button>
      </b-col>
    </b-row>

    <b-card no-body>
      <div class="p-1">
        <b-input-group>
          <b-form-input v-model="filtro" placeholder="Filtrar por vendedor ou plano..." />
        </b-input-group>
      </div>
      <b-table
        :items="assinaturasFiltradas"
        :fields="campos"
        :busy="loading"
        responsive
        striped
        hover
        show-empty
        empty-text="Nenhuma assinatura encontrada"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>Carregando...</strong>
          </div>
        </template>

        <template #cell(vendedor)="{ item }">
          <span>{{ nomeVendedor(item) }}</span>
          <small class="d-block text-muted">{{ item.vendedor && item.vendedor.usuario ? item.vendedor.usuario.login : '' }}</small>
        </template>

        <template #cell(plano)="{ item }">
          {{ item.plano ? item.plano.nome : '-' }}
        </template>

        <template #cell(status)="{ item }">
          <b-badge :variant="variantStatus(item.status)">{{ item.status }}</b-badge>
        </template>

        <template #cell(periodicidade)="{ item }">
          <b-badge variant="light-secondary">{{ item.periodicidade }}</b-badge>
        </template>

        <template #cell(data_inicio)="{ item }">
          {{ item.data_inicio || '-' }}
        </template>

        <template #cell(data_fim)="{ item }">
          {{ item.data_fim || 'Sem vencimento' }}
        </template>

        <template #cell(acoes)="{ item }">
          <b-button-group size="sm">
            <b-button variant="outline-primary" @click="abrirModalEditar(item)">
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button v-if="item.status === 'ativa'" variant="outline-danger" @click="cancelar(item)">
              <feather-icon icon="XIcon" />
            </b-button>
          </b-button-group>
        </template>
      </b-table>
    </b-card>

    <b-modal
      v-model="modalAberto"
      :title="editando ? 'Editar Assinatura' : 'Nova Assinatura'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      :ok-disabled="salvando"
      @ok.prevent="salvar"
    >
      <b-form>
        <b-form-group label="Vendedor (ID)" label-for="id_vendedor">
          <b-form-input id="id_vendedor" v-model="form.id_vendedor" type="number" placeholder="ID do vendedor" :disabled="!!editando" />
        </b-form-group>

        <b-form-group label="Plano" label-for="id_plano">
          <b-form-select id="id_plano" v-model="form.id_plano" :options="opcoesPlan" />
        </b-form-group>

        <b-form-group label="Periodicidade" label-for="periodicidade">
          <b-form-select id="periodicidade" v-model="form.periodicidade" :options="[{ value: 'mensal', text: 'Mensal' }, { value: 'anual', text: 'Anual' }]" />
        </b-form-group>

        <b-row>
          <b-col cols="6">
            <b-form-group label="Data Início" label-for="data_inicio">
              <b-form-input id="data_inicio" v-model="form.data_inicio" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Data Fim (opcional)" label-for="data_fim">
              <b-form-input id="data_fim" v-model="form.data_fim" type="date" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group v-if="editando" label="Status" label-for="status">
          <b-form-select id="status" v-model="form.status" :options="[
            { value: 'ativa', text: 'Ativa' },
            { value: 'cancelada', text: 'Cancelada' },
            { value: 'expirada', text: 'Expirada' },
            { value: 'pendente', text: 'Pendente' },
          ]" />
        </b-form-group>

        <b-form-group label="Observação" label-for="observacao">
          <b-form-textarea id="observacao" v-model="form.observacao" rows="2" />
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BTable, BButton, BButtonGroup, BBadge, BSpinner,
  BModal, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BInputGroup,
} from 'bootstrap-vue'
import { ref, computed, onMounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'

export default {
  components: {
    BRow, BCol, BCard, BTable, BButton, BButtonGroup, BBadge, BSpinner,
    BModal, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BInputGroup,
  },
  setup() {
    const toast = useToast()
    const assinaturas = ref([])
    const planos = ref([])
    const loading = ref(false)
    const filtro = ref('')
    const modalAberto = ref(false)
    const editando = ref(null)
    const salvando = ref(false)

    const formVazio = () => ({
      id_vendedor: '',
      id_plano: '',
      periodicidade: 'mensal',
      status: 'ativa',
      data_inicio: new Date().toISOString().split('T')[0],
      data_fim: '',
      observacao: '',
    })
    const form = ref(formVazio())

    const campos = [
      { key: 'vendedor', label: 'Vendedor' },
      { key: 'plano', label: 'Plano' },
      { key: 'status', label: 'Status' },
      { key: 'periodicidade', label: 'Period.' },
      { key: 'data_inicio', label: 'Início' },
      { key: 'data_fim', label: 'Fim' },
      { key: 'acoes', label: 'Ações' },
    ]

    const carregar = async () => {
      loading.value = true
      try {
        const [a, p] = await Promise.all([
          store.dispatch('appPlanos/fetchAssinaturas'),
          store.dispatch('appPlanos/fetchPlanos'),
        ])
        assinaturas.value = a
        planos.value = p
      } finally {
        loading.value = false
      }
    }

    const opcoesPlan = computed(() =>
      planos.value.map(p => ({ value: p.id, text: `${p.nome} (R$${Number(p.preco_mensal).toFixed(2)}/mês)` }))
    )

    const nomeVendedor = item => {
      if (!item.vendedor) return `Vendedor #${item.id_vendedor}`
      const u = item.vendedor.usuario
      if (u) return `${u.nome_responsavel || ''} ${u.sobrenome || ''}`.trim() || u.login
      return `Vendedor #${item.id_vendedor}`
    }

    const variantStatus = s => {
      if (s === 'ativa') return 'light-success'
      if (s === 'cancelada') return 'light-danger'
      if (s === 'expirada') return 'light-warning'
      return 'light-secondary'
    }

    const assinaturasFiltradas = computed(() => {
      if (!filtro.value) return assinaturas.value
      const f = filtro.value.toLowerCase()
      return assinaturas.value.filter(a => {
        const nome = nomeVendedor(a).toLowerCase()
        const plano = (a.plano?.nome || '').toLowerCase()
        return nome.includes(f) || plano.includes(f)
      })
    })

    const abrirModalCriar = () => {
      editando.value = null
      form.value = formVazio()
      modalAberto.value = true
    }

    const abrirModalEditar = assinatura => {
      editando.value = assinatura
      form.value = {
        id_vendedor: assinatura.id_vendedor,
        id_plano: assinatura.id_plano,
        periodicidade: assinatura.periodicidade,
        status: assinatura.status,
        data_inicio: assinatura.data_inicio,
        data_fim: assinatura.data_fim || '',
        observacao: assinatura.observacao || '',
      }
      modalAberto.value = true
    }

    const salvar = async () => {
      salvando.value = true
      try {
        const payload = { ...form.value, data_fim: form.value.data_fim || null }
        if (editando.value) {
          await store.dispatch('appPlanos/atualizarAssinatura', { id: editando.value.id, ...payload })
        } else {
          await store.dispatch('appPlanos/criarAssinatura', payload)
        }
        await carregar()
        modalAberto.value = false
        toast({ component: ToastificationContent, props: { title: 'Assinatura salva!', icon: 'CheckIcon', variant: 'success' } })
      } catch (e) {
        toast({ component: ToastificationContent, props: { title: 'Erro ao salvar', icon: 'AlertTriangleIcon', variant: 'danger', text: e?.response?.data?.error || e.message } })
      } finally {
        salvando.value = false
      }
    }

    const cancelar = async assinatura => {
      if (!window.confirm('Cancelar esta assinatura?')) return
      try {
        await store.dispatch('appPlanos/cancelarAssinatura', assinatura.id)
        await carregar()
        toast({ component: ToastificationContent, props: { title: 'Assinatura cancelada', icon: 'CheckIcon', variant: 'success' } })
      } catch (e) {
        toast({ component: ToastificationContent, props: { title: 'Erro ao cancelar', icon: 'AlertTriangleIcon', variant: 'danger' } })
      }
    }

    onMounted(carregar)

    return {
      assinaturas, planos, loading, filtro, modalAberto, editando, salvando, form,
      campos, opcoesPlan, assinaturasFiltradas, nomeVendedor, variantStatus,
      abrirModalCriar, abrirModalEditar, salvar, cancelar,
    }
  },
}
</script>
